import { useEffect, useRef, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LoginIcon from '@mui/icons-material/Login';
import DashboardLayout from '@examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '@examples/Navbars/DashboardNavbar';
import { useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { useCrmBoards } from '@layouts/pages/CRM/kanban-board/hooks/use-crm-boards.hook';
import { useLeadStatus } from '@layouts/pages/CRM/kanban-board/hooks/use-lead-status.hook';
import { CrmBoardService } from '@layouts/pages/CRM/kanban-board/services/crm-board.service';
import { BoardColumnWithLeads } from '@layouts/pages/CRM/kanban-board/services/responses/board-column.response';
import { useCrmUsers } from '@layouts/pages/CRM/kanban-board/hooks/use-crm-users.hook';
import { useAvailableHeight } from '@layouts/pages/CRM/hooks/use-available-height.hook';
import { StandaloneAutoCompleteSelect } from '@components/StandaloneAutoCompleteSelect';
import { MessageField } from '@components/MessageField';
import { AutomationService, AutomationSettings } from './services/automation.service';
import {
    ListAction, ListGroupLabel, ListSection, RuleBlock, RuleListItem, RulePanel,
} from './components/rule-list';
import { AutoReplyChannels } from './components/auto-reply-channels';
import { ACTION_ICON, StageRuleForm } from './components/stage-rule-form';
import { useStageRules } from './hooks/use-stage-rules.hook';
import { assignSentence, replySentence } from './automation-sentence';

const DEFAULTS: AutomationSettings = {
    autoAssignEnabled: false,
    autoReplyEnabled: false,
    autoReplyMessage: '',
    autoReplyChannels: [],
};

/// O que está aberto no painel da direita.
type Selection =
    | { kind: 'entry' }
    | { kind: 'rule'; id: string }
    | { kind: 'new' };

const FIRST: Selection = { kind: 'entry' };

export const Automations = () => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();
    const { summaries } = useCrmBoards();
    const { users } = useCrmUsers();
    const { statuses } = useLeadStatus();

    const [config, setConfig] = useState<AutomationSettings | null>(null);
    const [columns, setColumns] = useState<BoardColumnWithLeads[]>([]);
    const [boardId, setBoardId] = useState<string>();
    const [selected, setSelected] = useState<Selection>(FIRST);
    /// Sobe no "Cancelar" da regra aberta: o formulário remonta com o que está salvo.
    const [formVersion, setFormVersion] = useState(0);

    const stage = useStageRules(boardId);
    const { targetRef, height } = useAvailableHeight(config ? 'pronto' : '', { bottomGap: 48, minHeight: 480 });

    /// O que o servidor tem hoje; sem isto, sair do campo gravaria de novo sem alteração.
    const saved = useRef<AutomationSettings | null>(null);

    useEffect(() => {
        new AutomationService()
            .load()
            .then((res) => {
                const loaded = { ...DEFAULTS, ...(res.data.data ?? {}) };
                setConfig(loaded);
                saved.current = loaded;
            })
            .catch(() => {
                setConfig(DEFAULTS);
                saved.current = DEFAULTS;
            });
    }, []);

    // Abre no quadro padrão só como ponto de partida: fixá-lo escondia as regras dos outros quadros.
    useEffect(() => {
        if (boardId) return;

        const fallback = summaries.find((b) => b.isDefault) ?? summaries[0];

        if (fallback) setBoardId(fallback.id);
    }, [summaries, boardId]);

    useEffect(() => {
        if (!boardId) return;

        setColumns([]);

        new CrmBoardService()
            .getById(boardId)
            .then((res) => setColumns(res.data.data?.columns ?? []))
            .catch((err) => {
                setColumns([]);
                showAlert(apiErrorMessage(err), 'error', 4000);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boardId]);

    /// Grava sem botão: ligar uma chave e sair da tela não pode perder a mudança.
    const persist = async (next: AutomationSettings) => {
        const previous = saved.current;

        setConfig(next);
        saved.current = next;

        try {
            await new AutomationService().save(next);
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);

            // Volta ao que o servidor tem: chave ligada na tela e desligada lá é pior que não mexer.
            setConfig(previous);
            saved.current = previous;
        }
    };

    const toggle = (field: 'autoAssignEnabled' | 'autoReplyEnabled') => {
        if (!config) return;

        persist({ ...config, [field]: !config[field] });
    };

    /// O texto grava ao sair do campo, não a cada tecla.
    const saveMessage = () => {
        if (!config) return;

        // Compara mensagem geral e canais juntos: só a geral deixaria o Instagram sem gravar.
        const same = config.autoReplyMessage === saved.current?.autoReplyMessage
            && JSON.stringify(config.autoReplyChannels)
                === JSON.stringify(saved.current?.autoReplyChannels);

        if (same) return;

        persist(config);
    };

    const changeBoard = (id: string) => {
        setBoardId(id);

        // A regra aberta era de uma coluna do quadro anterior.
        if (selected.kind === 'rule' || selected.kind === 'new') setSelected(FIRST);
    };

    const rules = stage.rules ?? [];
    const openRule = selected.kind === 'rule' ? rules.find((r) => r.id === selected.id) : undefined;

    const panel = () => {
        if (!config) return null;

        if (selected.kind === 'entry') {
            return (
                <RulePanel
                    icon={<LoginIcon />}
                    title={t('automacoes.secaoEntrada')}
                    subtitle={t('automacoes.secaoEntradaAjuda')}
                >
                    <RuleBlock
                        icon={<GroupsOutlinedIcon />}
                        title={t('automacoes.distribuicao')}
                        sentence={assignSentence(t)}
                        enabled={config.autoAssignEnabled}
                        onToggle={() => toggle('autoAssignEnabled')}
                    />

                    <RuleBlock
                        icon={<ChatBubbleOutlineIcon />}
                        title={t('automacoes.respostaAutomatica')}
                        sentence={replySentence(t, config)}
                        enabled={config.autoReplyEnabled}
                        onToggle={() => toggle('autoReplyEnabled')}
                    >
                        <MessageField
                            fullWidth
                            multiline
                            minRows={4}
                            variableSize="large"
                            label={t('automacoes.mensagemPadrao')}
                            value={config.autoReplyMessage ?? ''}
                            onChange={(text) => setConfig({ ...config, autoReplyMessage: text })}
                            onBlur={saveMessage}
                            inputProps={{ maxLength: 2000 }}
                            placeholder={t('automacoes.respostaPlaceholder')}
                        />

                        <AutoReplyChannels
                            value={config.autoReplyChannels ?? []}
                            onChange={(next) => setConfig({ ...config, autoReplyChannels: next })}
                            onBlur={saveMessage}
                            fieldSx={{}}
                        />
                    </RuleBlock>
                </RulePanel>
            );
        }

        if (selected.kind === 'new') {
            // As colunas zeram enquanto o quadro recarrega; o formulário novo precisa de uma para começar.
            if (columns.length === 0) return <Skeleton variant="rounded" height={320} />;

            return (
                <StageRuleForm
                    key="new"
                    rule={null}
                    boardColumnId={columns[0].id}
                    columns={columns}
                    users={users}
                    statuses={statuses}
                    onCancel={() => setSelected(FIRST)}
                    onSaved={(created) => {
                        stage.load();
                        setSelected({ kind: 'rule', id: created.id });
                    }}
                />
            );
        }

        if (!openRule) return <Skeleton variant="rounded" height={320} />;

        return (
            <StageRuleForm
                key={`${openRule.id}-${formVersion}`}
                rule={openRule}
                boardColumnId={openRule.boardColumnId}
                columns={columns}
                users={users}
                statuses={statuses}
                onCancel={() => setFormVersion((v) => v + 1)}
                onSaved={() => stage.load()}
                onToggle={() => stage.toggle(openRule)}
                onDelete={() => stage.remove(openRule, () => setSelected(FIRST))}
            />
        );
    };

    return (
        <DashboardLayout>
            <DashboardNavbar title={t('automacoes.titulo')} />

            <Box sx={{ background: cc.pageBg, borderRadius: 3, p: 2 }}>
                {!config ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '400px 1fr' }, gap: 2 }}>
                        <Skeleton variant="rounded" height={420} />
                        <Skeleton variant="rounded" height={320} />
                    </Box>
                ) : (
                    // Lista e painel vão até o fim da janela, cada um rolando: senão sobrava tela vazia.
                    <Box
                        ref={targetRef}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', lg: '400px minmax(0, 1fr)' },
                            gap: 2,
                            height: { lg: height ?? 'auto' },
                        }}
                    >
                        {/* Todas as automações numa lista só; em abas, uma escondia a outra. */}
                        <Box
                            sx={{
                                border: `1px solid ${cc.border}`,
                                borderRadius: '10px',
                                background: cc.surfaceBg,
                                py: 0,
                                minHeight: 0,
                                overflowY: 'auto',
                            }}
                        >
                            <ListSection title={t('automacoes.regrasGerais')}>
                                {/* As duas regras gerais num item só: distribuir não tem o que configurar. */}
                                <RuleListItem
                                    icon={<LoginIcon />}
                                    title={t('automacoes.secaoEntrada')}
                                    selected={selected.kind === 'entry'}
                                    onSelect={() => setSelected({ kind: 'entry' })}
                                    trailing={
                                        <Typography sx={{ fontSize: 13, color: cc.textMuted }}>
                                            {t('automacoes.ligadasDe', {
                                                ligadas: Number(config.autoAssignEnabled)
                                                    + Number(config.autoReplyEnabled),
                                                total: 2,
                                            })}
                                        </Typography>
                                    }
                                />
                            </ListSection>

                            <ListSection title={t('automacoes.porEtapa')} divided>
                                {/* Um quadro só aparece se houver mais de um: seletor com uma opção é enfeite. */}
                                {summaries.length > 1 && (
                                    <Box sx={{ pb: 1 }}>
                                        <StandaloneAutoCompleteSelect
                                            options={summaries.map((board) => ({
                                                value: board.id,
                                                label: board.name,
                                            }))}
                                            value={boardId ?? ''}
                                            onChange={(value) => changeBoard(value as string)}
                                            placeholder={t('automacoes.quadro')}
                                        />
                                    </Box>
                                )}

                                {stage.rules === null ? (
                                    <Skeleton variant="rounded" height={120} />
                                ) : (
                                    <>
                                        {rules.length === 0 && (
                                            <Typography sx={{ fontSize: 14, color: cc.textMuted, px: 1.5, py: 1.5 }}>
                                                {t('automacoes.semRegrasNoQuadro')}
                                            </Typography>
                                        )}

                                        {/* Só as colunas que têm regra: as vazias viravam uma fila de zeros. */}
                                        {columns.map((column) => {
                                            const fromColumn = rules.filter((r) => r.boardColumnId === column.id);

                                            if (fromColumn.length === 0) return null;

                                            return (
                                                <Box key={column.id}>
                                                    <ListGroupLabel>{column.name}</ListGroupLabel>

                                                    {fromColumn.map((rule) => (
                                                        <RuleListItem
                                                            key={rule.id}
                                                            icon={ACTION_ICON[rule.action]}
                                                            title={rule.name}
                                                            enabled={rule.enabled}
                                                            selected={selected.kind === 'rule' && selected.id === rule.id}
                                                            onSelect={() => setSelected({ kind: 'rule', id: rule.id })}
                                                            onToggle={() => stage.toggle(rule)}
                                                        />
                                                    ))}
                                                </Box>
                                            );
                                        })}

                                        {columns.length > 0 && (
                                            <ListAction
                                                icon={<AddIcon />}
                                                label={t('automacoes.novaRegra')}
                                                onClick={() => setSelected({ kind: 'new' })}
                                            />
                                        )}
                                    </>
                                )}
                            </ListSection>
                        </Box>

                        <Box sx={{ minHeight: 0 }}>{panel()}</Box>
                    </Box>
                )}
            </Box>
        </DashboardLayout>
    );
};
